<template>
  <b-col class="account-details" lg="9">
    <b-row>
      <b-col class="d-flex align-items-center company-name-wrap">
        <b-col class="company-avatar">
          {{ getStartCase }}
        </b-col>
        <b-col class="p-0">
          <h1 class="heading font-weight-bold">
            {{ account.company }}
          </h1>
          <div class="d-flex limit-section align-items-center">
            <div class="drip-body m-0 font-weight-bold">
              Total Facility Limit:
              <span class="amount-text">
                {{ creditLimit | currency }}
              </span>
            </div>
            <div class="vertical-line"></div>
            <div class="drip-body m-0 font-weight-bold">
              Establishment fee:
              <span class="amount-text">
                {{ setupFee | percentSign }} of total facility limit
              </span>
            </div>
          </div>
        </b-col>
      </b-col>
    </b-row>
    <div class="d-flex mt-24">
      <b-col
        class="d-flex p-12 stats-wrap drip-body-small fw-bold color-noble-blue-500"
        lg="12"
        md="12"
      >
        <b-col class="cust-col-1 border-right-blue pr-12">
          <div>Advance rate</div>
          <div class="spacing-2"></div>
          <div class="font-weight-400 color-black">
            {{ advanceRate | percentSign }} of net invoice value
          </div>
        </b-col>
        <b-col class="cust-col-3 border-right-blue pl-12 pr-12">
          <div>Factoring commission fee</div>
          <div class="spacing-2"></div>
          <div class="font-weight-400 color-black">
            {{ factoringCommisionRate | percentSign }}
            of net invoice value
          </div>
        </b-col>
        <b-col class="cust-col-2 border-right-blue pl-12 pr-12">
          <div>Current Drip Base Rate (DBR)</div>
          <div class="spacing-2"></div>
          <div class="font-weight-400 color-black">
            {{ currentDripBase | percentSign }}
          </div>
        </b-col>
        <b-col class="cust-col-1 border-right-blue pl-12 pr-12">
          <div>Spread Value</div>
          <div class="spacing-2"></div>
          <div class="font-weight-400 color-black">
            {{ spreadValue || 0 | percentSign }}
          </div>
        </b-col>
        <b-col class="cust-col-5 pl-12 border-right-blue pr-12">
          <div>Discount rate</div>
          <div class="spacing-2"></div>
          <div class="font-weight-400 color-black">
            {{ interestRate | percentSign }} p.a.
          </div>
        </b-col>
        <b-col class="cust-col-6 pl-12 border-right-blue pr-12">
          <div>Maximum Tenor</div>
          <div class="spacing-2"></div>
          <div class="font-weight-400 color-black">
            {{ renderFloat(paymentTenor) || 0 }} days from
            {{ termFromValue || '' }}
          </div>
        </b-col>
        <b-col class="cust-col-1 pl-12 pr-19">
          <div>Currency</div>
          <div class="spacing-2"></div>
          <div class="font-weight-400 color-black">
            {{ currencyValue || '' }}
          </div>
        </b-col>
      </b-col>
    </div>
    <template v-if="!!monitoringData">
      <b-row>
        <b-col :xs="12" class="d-flex account-title-wrap" cols="10">
          <b-img
            :src="require('@/assets/images/common/account-refresh.svg')"
            width="48"
            height="48"
            class="mr-12"
          >
          </b-img>
          <h4 class="heading font-weight-bold mr-2">
            {{ $t('account.index.accountDataUpdates') }}
          </h4>
          <div class="badge-wrap">
            <b-badge variant="success" class="badge">New</b-badge>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="10">
          <div class="drip-body color-noble-blue-500 font-weight-400">
            {{ $t('account.index.accountDataSubtitle') }}
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="10">
          <div class="drip-body-small color-noble-blue-500 mt-12">
            {{ $t('account.index.accountDataNote') }}
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-4 pl-15" v-if="showInvoiceBlockAlert">
        <b-col class="d-flex invoice-block-alert align-items-center">
          <b-icon-x-circle class="mr-3 color-warning" />
          <h6 class="m-0 fw-bold">
            {{ $t('account.index.invoiceBlockText') }}
          </h6>
        </b-col>
      </b-row>

      <div
        class="plaid-container"
        :class="{
          'gst-border': !monitoringData.gst.active,
          updated: monitoringData.gst.active
        }"
      >
        <b-col
          cols="12"
          class="p-0 d-flex flex-md-row flex-column justify-content-between"
        >
          <b-col class="p-0">
            <div class="d-flex mb-1">
              <div class="drip-h3 color-noble-blue-500 font-weight-700">
                {{ $t('account.index.gstTitle') }}
              </div>
              <div
                class="chip-status success font-weight-normal"
                v-if="monitoringData.gst.active"
              >
                <b-icon-check-circle class="mr-2 color-success" />
                Update completed
              </div>
              <div class="chip-status warning font-weight-normal" v-else>
                <b-icon-info-circle class="mr-2 color-warning" />
                Update pending
              </div>
            </div>
            <div class="drip-body color-noble-blue-500 font-weight-400">
              {{
                $t('account.index.gstLastSuccessful', {
                  lastUpdated: monitoringData.gst.data.last_updated_time
                })
              }}
            </div>
          </b-col>
          <b-col
            md="4"
            lg="4"
            sm="4"
            class="p-0 mt-2 mt-md-0"
            v-if="!monitoringData.gst.active"
          >
            <action-button :click="openGstLink">
              {{ $t('account.index.verifyGst') }}
              <b-icon-chevron-right
                class="color-white ml-2 breadcrumb-section"
              />
            </action-button>
          </b-col>
        </b-col>
        <b-col
          class="drip-body color-noble-blue-500 font-weight-400 pl-0 mt-16"
        >
          {{ $t('account.index.gstInfo') }}
        </b-col>
        <div class="verify-gst mt-1">
          <action-button
            variant="text"
            :click="openHowToVerifyGstLink"
            class="mr-2 p-0 h-auto drip-label"
          >
            {{ $t('account.index.howToVerifyGst') }}
          </action-button>
        </div>
      </div>
      <div
        class="plaid-container"
        :class="{
          'warning-border': !monitoringData.financials.active,
          updated: monitoringData.financials.active
        }"
      >
        <b-col cols="12" class="pl-0">
          <div class="d-flex mb-1">
            <div class="drip-h3 fw-bold color-noble-blue-500">
              {{ $t('account.index.financialStatementsTitle1') }}
              {{
                getStartEndDate
                  ? $t('account.index.financialStatementsTitle2', {
                      startDate: this.monitoringData.financials.data.start_date,
                      endDate: this.monitoringData.financials.data.end_date
                    })
                  : ''
              }}
            </div>
            <div
              class="chip-status success font-weight-normal"
              v-if="monitoringData.financials.active"
            >
              <b-icon-check-circle class="mr-2 color-success" />
              Update completed
            </div>
            <div class="chip-status warning font-weight-normal" v-else>
              <b-icon-info-circle class="mr-2 color-warning" />
              Update pending
            </div>
          </div>
          <div>
            <ul>
              <li v-for="(item, index) in financialStatements" :key="index">
                {{ item }}
              </li>
            </ul>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="mt-3 p-0"
          :key="uploadSavedUpdated"
          :class="{
            'monitor-uploads': uploadsSaved[0].financials.length === 0,
            uploaded:
              uploadsSaved[0].financials.length > 0 &&
              !monitoringData.financials.active,
            updated: monitoringData.financials.active
          }"
          v-if="showFinancials"
        >
          <uploads-new
            :index="0"
            :title="$t('account.index.uploadFinancial')"
            :subtext="$t('account.index.noteSize')"
            itemKey="financials"
            docTag="financials"
            :uploadsSaved="uploadsSaved"
            :isOptional="false"
            :documentId="''"
            :uploadBorder="false"
            acceptedFiles="application/pdf"
            :monitoringData="monitoringData"
            :remove="monitoringData.financials.active"
            :multiple="['financials']"
            :apiUrl="apiUrl"
            v-if="showUploads"
          />
          <b-col
            md="5"
            lg="5"
            xl="4"
            class="mb-3 mt-4"
            v-if="
              uploadsSaved[0] &&
              uploadsSaved[0].financials.length !== 0 &&
              !monitoringData.financials.active
            "
            @click="submitFiles('financials')"
          >
            <action-button variant="solid">
              {{ $t('account.index.submitFiles') }}
              <img
                src="@/assets/images/common/check-mark.svg"
                alt="Steps"
                width="24"
                class="mr-2 ml-2"
              />
            </action-button>
          </b-col>
        </b-col>
      </div>
    </template>
    <template v-else>
      <b-col class="d-flex flex-column align-items-center mt-108">
        <div class="drip-h4 font-weight-700 color-noble-blue-500">
          {{ $t('account.index.PageGettingUpdated') }}
        </div>
        <div class="drip-h2 color-noble-blue-500">
          {{ $t('account.index.stayTuned') }}
        </div>
        <lottie-player
          :src="fetchLottieJSON"
          background="transparent"
          speed="1"
          style="width: 500px; height: 334px"
          :loop="true"
          autoplay
        ></lottie-player>
      </b-col>
    </template>
  </b-col>
</template>
<script>
import accountsService from '@/services/accounts'
import ActionButton from '@/components/common/ActionButton.vue'
import monitoringService from '@/services/monitoring'
import remittanceService from '@/services/remittance'
import StayTuned from '@/assets/images/lottie/stay-tuned.json'
import { FINANCIAL_STATEMENTS_LIST } from '@/utils/constants'
import UploadsNew from '@/components/common/UploadsNew'
import { uploadFilesBus } from '../../../main'

export default {
  errorCaptured: function (err) {
    console.log('Caught error', err.message)
  },
  data() {
    return {
      account: {},
      linkToken: '',
      monitoringData: null,
      supplier: {},
      stayTuneddJSON: StayTuned,
      currency: {
        symbol: '$',
        thousandsSeparator: ',',
        fractionCount: 2,
        fractionSeparator: '.',
        symbolPosition: 'front',
        symbolSpacing: false
      },
      uploadsSaved: [
        {
          index: 0,
          editable: true,
          visible: true,
          vIndex: 0,
          exporter_user_id: '',
          financials: [],
          errorMessage: ''
        }
      ],
      showUploads: false,
      uploadSavedUpdated: null
    }
  },
  props: {
    id: {
      type: [String, Number],
      default: ''
    },
    status: {
      type: String,
      default: ''
    },
    institution_name: {
      type: String,
      default: ''
    },
    accounts: {
      type: Array,
      default: () => []
    }
  },
  components: {
    ActionButton,
    UploadsNew
  },
  mounted() {
    this.getAccountData()
    this.fetchMonitoringData()
  },
  methods: {
    renderFloat(val) {
      if (val) {
        return parseInt(val)
      }
      return 0
    },
    getAccountData() {
      accountsService
        .fetchBusinessAccount({
          include: ['parent_user', 'plan', 'credit']
        })
        .then((res) => {
          this.account = res.data
        })
        .catch()
    },
    isConnected(status) {
      return status === 'connected'
    },
    async fetchMonitoringData() {
      this.loading = true
      try {
        const res = await monitoringService.getMonitoringData(
          this.$store.getters.accountId
        )
        if (res.data !== null) {
          this.monitoringData = res.data

          this.uploadsSaved[0].financials.push(
            ...this.monitoringData.financials.data.documents
          )
        } else {
          this.monitoringData = null
        }
      } catch (error) {
        // Handle errors here
        console.error('Error fetching monitoring data:', error)
        this.errorMessage =
          'Failed to fetch monitoring data. Please try again later.'
      } finally {
        this.loading = false // Ensure loading is set to false in all cases
        this.showUploads = true
      }
    },
    async fetchMonitoringDataPoll() {
      this.loading = true
      const res = await monitoringService.getMonitoringData(
        this.$store.getters.accountId
      )
      this.monitoringData = res.data

      this.loading = false
    },

    goToAddNewAccount() {
      this.$mpTrack('Account Details - "add new account" cta clicked')
      this.$router.push({ name: 'invoicesEsign' })
    },

    async submitFiles(value) {
      this.$mpTrack(`Account Details - Submitting files for ${value}`)
      try {
        this.$store.dispatch('setDownloadNotification', {
          fileDownloadNotification: true
        })
        // Start polling the API 3 times at 30-second intervals
        this.pollMonitoringData(12, 10000, value) //
      } catch (error) {
        console.error('Error submitting files:', error)
      }
    },
    pollMonitoringData(times, interval, value) {
      let count = 0
      const polling = setInterval(async () => {
        try {
          const res = await monitoringService.getMonitoringData(
            this.$store.getters.accountId
          )
          this.monitoringData = res.data
          // Call the API
          // Check if the required condition is met
          if (res.data[value].active) {
            clearInterval(polling) // Stop polling
            this.$store.dispatch('setDownloadNotification', {
              fileDownloadNotification: false
            })
            this.showUploads = false
            setTimeout(() => {
              this.showUploads = true
            }, 50)
            this.handleSuccess() // Trigger the success notification
            return
          }
        } catch (error) {
          console.error('Error fetching monitoring data:', error)
          this.$mpTrack('Account Details - Polling failed', {
            error: error?.message
          })
        }

        count++
        this.$mpTrack('Account Details - File upload polling', {
          count
        })
        if (count >= times) {
          clearInterval(polling) // Stop polling after max attempts
          this.$store.dispatch('setDownloadNotification', {
            fileDownloadNotification: false
          })
          this.$notify({
            title: 'Failed to update!',
            type: 'error'
          })
          this.$mpTrack('Account Details - Polling failed!')
        }
      }, interval)
    },
    handleSuccess() {
      this.$mpTrack('Account Details - Polling Success!')
      this.$notify({
        title: 'Data updated successfully',
        type: 'success'
      })
    },
    fetchDocumentsBy(arr, tag) {
      return arr
        .map((x) => {
          x.id = x.document_id
          return x
        })
        .filter((x) => x.tags === tag)
    },
    documentRequired(show) {
      if (show) {
        this.uploadsSaved[0].errorMessage = 'error message'
      } else {
        this.uploadsSaved[0].errorMessage = ''
      }
    },
    openGstLink() {
      this.$notify({
        title:
          'Please refresh this page after completing your GST authentication.',
        type: 'success',
        duration: -1
      })
      window.open(this.monitoringData.gst.data.gst_onboarding_url, '_blank')
    },
    openHowToVerifyGstLink() {
      window.open(
        'https://docs.google.com/document/d/1XSFZPV-bw8eogdinqVsaF0se5g4R2Gk9/edit?usp=sharing&ouid=117111790983038110113&rtpof=true&sd=true',
        '_blank'
      )
    },
    addDocument(index, data) {
      if (
        data &&
        data.docTag &&
        data.id &&
        index >= 0 &&
        this.uploadsSaved[index] &&
        this.uploadsSaved[index][data.docTag]
      ) {
        this.uploadsSaved[index][data.docTag].push({
          id: data.id,
          name: data.name,
          url: data.url,
          tag: data.tag
        })
        this.$mpTrack('Add Bank - File uploaded (by type)', {
          tag: data.docTag
        })
      }
    },
    removeDocument(documentIndex, docTag, documentId) {
      if (
        documentIndex >= 0 &&
        docTag &&
        this.uploadsSaved[documentIndex][docTag] &&
        documentId
      ) {
        remittanceService
          .removeDocument(this.$store.getters.accountId, documentId)
          .then((res) => {
            this.uploadsSaved[documentIndex][docTag] = this.uploadsSaved[
              documentIndex
            ][docTag].filter((x) => x.id !== documentId)

            this.$mpTrack('Account Details - File removed (by type)', {
              tag: docTag
            })
          })
          .catch((e) => {
            this.$notify({
              title:
                e?.response?.data?.error || this.$t('error.unableToProcess'),
              type: 'error'
            })
          })
      }
    }
  },
  created() {
    uploadFilesBus.$on('addDocument', this.addDocument)
    uploadFilesBus.$on('removeDocument', this.removeDocument)
  },
  destroyed() {
    uploadFilesBus.$off('addDocument', this.addDocument)
    uploadFilesBus.$off('removeDocument', this.removeDocument)
  },
  computed: {
    spreadValue() {
      return (
        (this.account?.plan?.interest_rate ?? 0) -
        (this.account?.plan?.dbr_details?.current?.dbr ?? 0)
      ).toFixed(2)
    },
    creditLimit() {
      return this.account?.credit?.credit_limit ?? 0
    },
    setupFee() {
      return this.account?.credit?.setup_fee ?? 0
    },
    advanceRate() {
      return this.account?.credit?.advance_rate ?? 0
    },
    factoringCommisionRate() {
      return this.account?.plan?.factoring_commission[0]?.rate ?? 0
    },
    currentDripBase() {
      return this.account?.plan?.dbr_details?.current.dbr ?? 0
    },
    interestRate() {
      return this.account?.plan?.interest_rate ?? 0
    },
    paymentTenor() {
      return this.account?.plan?.payment_tenor ?? 0
    },
    termFromValue() {
      return this.account?.plan?.term_from_value ?? 0
    },
    currencyValue() {
      return this.account?.plan?.currency_value ?? 0
    },
    getStartEndDate() {
      return (
        this.monitoringData &&
        this.monitoringData.financials.data.start_date &&
        this.monitoringData.financials.data.end_date
      )
    },
    apiUrl() {
      return `${process.env.VUE_APP_DRIP_API_HOST}/v1/onboarding/leads/${this.$store.getters.accountId}/documents`
    },
    showInvoiceBlockAlert() {
      if (this.monitoringData && this.monitoringData.mail_date) {
        const mailDate = new Date(this.monitoringData.mail_date) // Use dynamic mail_date
        const currentDate = new Date() // Get today's date

        // Create a new Date object and add 60 days
        const days60AfterMailDate = new Date(mailDate)
        days60AfterMailDate.setDate(mailDate.getDate() + 60)

        return currentDate >= new Date(this.monitoringData?.revoke_date)
      }
      return false
    },
    getStartCase() {
      const names = (this.account?.company || '').split(' ')
      let res = ''
      if (names.length > 1) {
        res = names[0].slice(0, 1) + names[names.length - 1].slice(0, 1) || ''
      } else {
        res = names[0].slice(0, 2)
      }
      return res.toUpperCase()
    },
    financialStatements() {
      return FINANCIAL_STATEMENTS_LIST
    },
    fetchLottieJSON() {
      return JSON.stringify(this.stayTuneddJSON)
    },
    connectedAccounts() {
      return (
        this.accounts &&
        this.accounts.filter((account) => account.status === 'connected')
      )
    },
    showFinancials() {
      if (this.monitoringData) {
        if (
          this.monitoringData.financials.active &&
          this.monitoringData.financials.data.documents.length === 0
        ) {
          return false
        } else if (
          this.monitoringData.financials.active &&
          this.monitoringData.financials.data.documents.length !== 0
        ) {
          return true
        } else if (
          !this.monitoringData.financials.active &&
          this.monitoringData.financials.data.documents.length === 0
        ) {
          return true
        } else if (
          !this.monitoringData.financials.active &&
          this.monitoringData.financials.data.documents.length !== 0
        ) {
          return true
        }
      }
      return false
    }
  },

  beforeDestroy() {
    // Clear the interval if the component is destroyed
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  }
}
</script>
<style lang="scss">
.monitor-uploads {
  border: 1px solid $orange-500 !important;
  border-left: 8px solid $orange-500 !important;
  border-radius: 8px;
}
.uploaded {
  border: 1px solid $orange-500 !important;
  background: $orange-50;
  border-radius: 8px;
  .upload-container {
    border-radius: 8px;
    background: $orange-50;
  }
  .file-upload-details {
    background: $orange-50;
  }
}
.updated {
  border: 1px solid $sky-blue-500;
  border-radius: 8px;
}
.account {
  background: white;
}
.vue-notification-group.notification-toast {
  max-width: fit-content;
}
.custom-template .custom-template-icon {
  width: 20px !important;
  border: 0 !important;
}
.custom-template .custom-template-icon .icon {
  width: 20px !important;
  height: 20px !important;
}
.custom-template .custom-template-close.success {
  margin-left: 10px;
}
</style>

<style lang="scss" scoped>
@import '../../../assets/scss/variables';
li {
  font-size: 16px;
}
.verify-gst {
  text-decoration: underline;
  text-decoration-color: $wealthy-green-500;
  max-width: fit-content;
}
.limit-section {
  gap: 12px;
  margin-top: 10px;
  max-width: fit-content;
}
.mt-24 {
  margin-top: 24px;
}
.mt-12 {
  margin-top: 12px;
}
.mt-108 {
  margin-top: 108px;
}
.p-12 {
  padding: 12px 0px;
}
.pr-12 {
  padding-right: 12px;
}
.amount-text {
  color: $wealthy-green-500;
}
.account-title-wrap {
  margin-top: 49px;
}
.badge {
  height: fit-content;
}
.badge-wrap {
  display: flex;
  height: 36px;
  align-items: center;
}
.mr-12 {
  margin-right: 12px;
}
.vertical-line {
  border-left: 2px solid #459ed9;
  height: 22px;
}
.plaid-title {
  color: $noble-blue-500;
  font-weight: 700;
}
.mt-16 {
  margin-top: 16px;
}
ul {
  padding-left: 24px;
}
.mt-40 {
  margin-top: 40px;
}
.company-name-wrap {
  gap: 24px;
}
.small-text {
  font-size: 12px;
  color: $wealthy-green-500;
  font-weight: 700;
}
.breadcrumb-section {
  color: $white;
  font-weight: 700;
  font-size: 12px !important;
  margin-left: 8px !important;
}
.company-avatar {
  width: 80px;
  height: 80px;
  padding: 18px 19px;
  max-width: fit-content;
  gap: 0px;
  border-radius: 10px;
  border: 2px solid $sky-blue-500;
  font-size: 32px;
  font-weight: 700;
  line-height: 43.65px;
  text-align: center;
  color: $sky-blue-500;
  background-color: $sky-blue-50;
}
.scf-button.medium {
  padding: 16px 14.5px 15.5px 16px;
}

.plaid-container {
  border: 2px solid #ebc1b3;
  border-radius: 8px;
  margin-top: 22px;
  padding: 24px;
}
.gst-border {
  border-left: 8px solid $orange-500 !important;
}
.warning-border {
  border: 2px solid #ebc1b3;
}
.updated {
  border: 1px solid $sky-blue-500;
  border-radius: 8px;
}
.plaid-container-active {
  border: 2px solid $sky-blue-400;
}
.side-chip {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: max-content;
  height: 22px;
  padding: 4px 8px;
  margin-top: 12px;
  margin-left: 12px;
  font-size: 10px;
  letter-spacing: 0.6px;
  font-weight: 400;
  color: $noble-blue-500;
  background: $sky-blue-50;
  border: 1px solid $sky-blue-400;
  border-radius: 24px;
  text-decoration: none;
  &.processing {
    background: $sky-blue-50;
    border: 1px solid $sky-blue-500;
  }
}
.stats-wrap {
  border: 1px solid $sky-blue-300;
  border-radius: 4px;
  .text {
    color: $noble-blue-500;
    font-size: 12px;
    font-weight: 700;
  }
  .data {
    font-size: 12px;
    color: #1a1a1a;
    font-weight: 400;
  }
}
.border-right-blue {
  border-right: 1px solid $sky-blue-500;
}
.pl-12 {
  padding-left: 12px !important;
}
.pl-15 {
  padding-left: 15px !important;
}
.horizontal-line {
  content: '';
  border: 1px solid $sky-blue-500;
}
.chip-status {
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  width: fit-content;
  height: 28px;
  padding: 2px 8px;
  color: $noble-blue-500;
  border-radius: 24px;
  font-size: 12px;
  margin-left: 10px;
  &.warning {
    background: $orange-50;
    border: 1px solid $orange-400;
  }
  &.success {
    background: $spring-green-50;
    border: 1px solid $spring-green-400;
  }
}
.color-warning {
  color: $orange-500;
}
.color-success {
  color: $wealthy-green-500;
}
.invoice-block-alert {
  border: 1px solid $orange-600;
  background: $orange-100;
  padding: 16px;
  border-radius: 8px;
  color: $noble-blue-500;
  max-width: fit-content;
}
.pl-15 {
  padding-left: 15px;
}
.ml-15 {
  margin-left: 15px;
}
.fw-bold {
  font-weight: 700;
}
.pr-19 {
  padding-right: 19px;
}
.cust-col-1 {
  width: 9.9%;
}
.cust-col-2 {
  width: 12.25%;
}
.cust-col-3 {
  width: 15.34%;
}
.cust-col-4 {
  width: 21%;
}
.cust-col-5 {
  width: 9.28%;
}
.cust-col-6 {
  width: 11.01%;
}

.account-details {
  background: white;
  padding: 40px 24px;
  min-height: 92vh;
  padding-left: 22px;
  padding-top: 40px;
  margin-left: 108px;
  .heading {
    font-family: $nunito;
    color: $secondary;
    font-size: 32px;
    padding-bottom: 5px;
    text-transform: capitalize;
    position: relative;
    max-width: fit-content;
    &::before {
      content: '';
      position: absolute;
      width: 184px;
      height: 1px;
      bottom: 0;
      left: 0;
      border-bottom: 2px solid $primary;
    }
  }

  .section-title {
    font-family: $nunito;
    color: $secondary;
    font-size: 2.13rem;
  }
  .account-index-block {
    margin-bottom: 3rem;
    margin-top: 2rem;
  }
}
.drip-label {
  font-size: 14px !important;
}
</style>
